import { useEffect, useMemo, useRef, useState, type ReactNode, type ThHTMLAttributes, type TdHTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/cn'
import { Dropdown, type MenuItem } from './Popover'
import { Button } from './Button'
import { Input } from './Field'

/**
 * Settings tables ("ledger"). Every list of more than a couple of items is one of these: fixed
 * columns, one row per record, numbers right-aligned, and a 3 px status rail on the left edge so a
 * whole table can be read from a distance (docs/web-ui.md §6). Editing happens in a popup, never in
 * the row, so cells stay plain text and rows stay clickable.
 */

/** Status rail colour: what the left edge of a row says at a glance. */
export type Rail = 'ok' | 'attention' | 'error' | 'idle'
const RAIL: Record<Rail, string> = {
  ok: 'bg-mint-500',
  attention: 'bg-amber-400',
  error: 'bg-red-600',
  idle: 'bg-cream-400'
}

export function Table({ children, mobile, footer, minWidth = 720 }: {
  children: ReactNode; mobile?: ReactNode; footer?: ReactNode; minWidth?: number
}) {
  return (
    // Inside a flush Card the card already draws the frame: drop ours so borders do not double up.
    <div className="overflow-hidden rounded-xl border border-line bg-cream-50 in-[.card-flush]:rounded-none in-[.card-flush]:rounded-b-xl in-[.card-flush]:border-0">
      {/* `relative` keeps absolutely positioned children (sr-only labels) inside the scroller: without
          it their containing block is the viewport and they widen the whole page. */}
      <div className={cn('relative overflow-x-auto', mobile ? 'max-sm:hidden' : '')}>
        <table className="w-full border-collapse text-sm" style={{ minWidth }}>{children}</table>
      </div>
      {mobile && <div className="flex flex-col divide-y divide-line sm:hidden">{mobile}</div>}
      {footer && <div className="flex flex-wrap items-center gap-3 border-t border-line bg-cream-100 px-4 py-2.5 text-[13px] text-ink-400">{footer}</div>}
    </div>
  )
}

const RAIL_COL = 'w-[3px] min-w-[3px] max-w-[3px] p-0'

export function Th({ className, rail, ...p }: ThHTMLAttributes<HTMLTableCellElement> & { rail?: boolean }) {
  // `cn` does not resolve conflicts, so the rail column must not get the padding classes at all
  // (px-4 would beat p-0 and widen the rail to ~32 px).
  return (
    <th
      className={cn('border-b border-line bg-cream-100',
        rail ? RAIL_COL : 'whitespace-nowrap px-4 py-2.5 text-left text-xs font-medium text-ink-500', className)}
      {...p}
    />
  )
}

export function Td({ className, ...p }: TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn('border-b border-line px-4 py-3 align-middle text-ink-100', className)} {...p} />
}

/** The 3 px rail cell. Pass `rail` on the row's first cell, `undefined` for no rail. */
export function RailCell({ rail }: { rail?: Rail }) {
  return (
    <td className={cn('relative border-b border-line', RAIL_COL)}>
      {rail && <span className={cn('absolute inset-y-0 left-0 w-[3px]', RAIL[rail])} aria-hidden="true" />}
    </td>
  )
}

/** A row that opens its detail popup on click. Interactive children (buttons, selects) win. */
export function Row({ rail, onClick, children, className }: {
  rail?: Rail; onClick?(): void; children: ReactNode; className?: string
}) {
  return (
    <tr
      className={cn('border-b border-line last:border-b-0 transition-colors', onClick && 'cursor-pointer hover:bg-cream-200', className)}
      onClick={onClick && ((e: React.MouseEvent<HTMLTableRowElement>) => {
        if ((e.target as HTMLElement).closest('button, a, select, input, label')) return
        onClick()
      })}
    >
      <RailCell rail={rail} />
      {children}
    </tr>
  )
}

/** Two-line cell: name over a quieter detail. */
export function Cell({ title, sub, className }: { title: ReactNode; sub?: ReactNode; className?: string }) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="font-semibold text-ink-100">{title}</div>
      {sub && <div className="mt-0.5 truncate text-xs text-ink-400">{sub}</div>}
    </div>
  )
}

/** Row actions behind a `⋯` menu (same popup as every other menu in the app). */
export function RowMenu({ label, items }: { label?: string; items: MenuItem[] }) {
  const { t } = useTranslation()
  return (
    <div className="flex justify-end">
      <Dropdown
        label={label ?? t('common.more')}
        items={items}
        trigger={p => (
          <button
            {...p} type="button"
            className="flex h-7 w-7 items-center justify-center rounded-md text-ink-400 hover:bg-cream-200 hover:text-ink-100"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" />
            </svg>
          </button>
        )}
      />
    </div>
  )
}

/** Table toolbar: search box, filter chips, count. */
export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('mb-3 flex flex-wrap items-center gap-2.5', className)}>{children}</div>
}

export function SearchBox({ value, onChange, placeholder, className }: { value: string; onChange(v: string): void; placeholder: string; className?: string }) {
  return (
    <label className={cn('flex h-9 min-w-0 flex-1 items-center gap-2 rounded-lg max-sm:h-11 border border-cream-300 bg-cream-50 px-3 sm:min-w-[240px] sm:max-w-[320px] sm:flex-none transition-shadow focus-within:border-ginger-500 focus-within:ring-[3px] focus-within:ring-ginger-100', className)}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0 text-ink-500" aria-hidden="true">
        <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" strokeLinecap="round" />
      </svg>
      <Input
        value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder}
        className="h-auto min-w-0 flex-1 border-0 bg-transparent p-0 focus:ring-0"
      />
    </label>
  )
}

export function FilterChips<T extends string>({ value, onChange, options, label, trailing }: {
  value: T; onChange(v: T): void; options: { value: T; label: string; count?: number }[]; label: string
  /** Optional control on the right end of the same underline row (search, count). */
  trailing?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-line">
      {/* Scrolls sideways (touch, trackpad, shift+wheel) only when the tabs do not fit, with no visible
          bar. The active underline is an inset shadow, not a border pushed below the row, so nothing
          overflows vertically. */}
      <div role="group" aria-label={label} className="scrollbar-none flex min-w-0 flex-1 gap-5 overflow-x-auto overflow-y-hidden">
        {options.map(o => (
          <button
            key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}
            className={cn('flex shrink-0 items-center gap-1.5 pb-2.5 pt-1 text-[13px] font-medium transition-colors whitespace-nowrap',
              value === o.value ? 'text-ink-100 shadow-[inset_0_-2px_0_var(--color-ink-100)]' : 'text-ink-400 hover:text-ink-100')}
          >
            {o.label}
            {o.count !== undefined && <span className="font-mono text-xs font-normal text-ink-500 tabular-nums">{o.count}</span>}
          </button>
        ))}
      </div>
      {trailing && <div className="pb-2">{trailing}</div>}
    </div>
  )
}

/**
 * A filter as one segmented control: a hairline box with the selected item raised inside it. Reads as
 * "this narrows the list", not as "this is another page" — which is what underline tabs on a page
 * that already has a title say. Counts stay in mono and uncoloured (docs/web-ui.md §3).
 */
export function SegmentedFilter<T extends string>({ value, onChange, options, label, trailing }: {
  value: T; onChange(v: T): void; options: { value: T; label: string; count?: number }[]; label: string
  /** Control on the right end of the same row (search). */
  trailing?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div role="group" aria-label={label}
        className="scrollbar-none inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-[9px] border border-line bg-cream-100 p-[3px]">
        {options.map(o => (
          <button
            key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}
            className={cn('inline-flex h-7 shrink-0 items-center gap-1.5 rounded-[7px] px-2.5 text-[12.5px] font-medium transition-colors max-sm:h-8',
              value === o.value
                ? 'bg-cream-50 text-ink-100 shadow-[0_0_0_1px_var(--color-line)]'
                : 'text-ink-500 hover:text-ink-100')}
          >
            {o.label}
            {o.count !== undefined && <span className="font-mono text-[11px] font-normal text-ink-500 tabular-nums">{o.count}</span>}
          </button>
        ))}
      </div>
      {trailing && <div className="ml-auto">{trailing}</div>}
    </div>
  )
}

export function Count({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('ml-auto shrink-0 font-mono text-xs text-ink-500', className)}>{children}</span>
}

export function Pager({ page, pages, onChange }: { page: number; pages: number; onChange(p: number): void }) {
  const { t } = useTranslation()
  if (pages <= 1) return null
  return (
    <div className="inline-flex items-center gap-1">
      <button type="button" aria-label={t('common.back')} disabled={page <= 1} onClick={() => onChange(page - 1)}
        className="h-8 w-8 rounded-lg border border-cream-300 bg-cream-50 text-ink-300 hover:bg-cream-200 hover:text-ink-100 disabled:opacity-40">‹</button>
      {Array.from({ length: pages }, (_, i) => i + 1).slice(Math.max(0, page - 3), page + 2).map(p => (
        <button
          key={p} type="button" aria-current={p === page} onClick={() => onChange(p)}
          className={cn('h-8 min-w-8 rounded-lg border px-2 text-[13px] transition-colors',
            p === page ? 'border-ink-100 bg-ink-100 font-semibold text-canvas' : 'border-cream-300 bg-cream-50 text-ink-300 hover:bg-cream-200 hover:text-ink-100')}
        >{p}</button>
      ))}
      <button type="button" aria-label={t('common.more')} disabled={page >= pages} onClick={() => onChange(page + 1)}
        className="h-8 w-8 rounded-lg border border-cream-300 bg-cream-50 text-ink-300 hover:bg-cream-200 hover:text-ink-100 disabled:opacity-40">›</button>
    </div>
  )
}

/** One status tile above a table (label + big value + optional note). */
export function Tile({ label, value, unit, note, rail, tone }: {
  label: ReactNode; value: ReactNode; unit?: string; note?: ReactNode; rail?: Rail; tone?: 'warning'
}) {
  return (
    <div className={cn('relative overflow-hidden rounded-xl border border-line bg-cream-50 px-4 py-3.5',
      tone === 'warning' && 'border-amber-400 bg-amber-50')}>
      {rail && <span className={cn('absolute inset-y-0 left-0 w-[3px]', RAIL[rail])} aria-hidden="true" />}
      <div className="font-mono text-[11px] uppercase tracking-[0.08em] text-ink-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold leading-tight text-ink-100 tracking-tight">
        {value}{unit && <span className="ml-1 text-[13px] font-normal text-ink-400">{unit}</span>}
      </div>
      {note && <div className="mt-1.5 text-xs text-ink-500">{note}</div>}
    </div>
  )
}

/** Stat tiles. Columns follow the space actually available (the settings area has two side columns). */
export function Tiles({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">{children}</div>
}

/**
 * Client-side search + filter + paging for a table. Keeps the page code to the data it loads.
 */
export function useTable<T>(items: T[] | null, opts: {
  search?: (item: T) => string
  filter?: (item: T, filter: string) => boolean
  perPage?: number
  sort?: (a: T, b: T) => number
}) {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState('all')
  const [page, setPage] = useState(1)
  const { search, filter: match, perPage = 25, sort } = opts
  const filtered = useMemo(() => {
    let out = items ?? []
    if (sort) out = [...out].sort(sort)
    if (match && filter !== 'all') out = out.filter(i => match(i, filter))
    const q = query.trim().toLowerCase()
    if (q && search) out = out.filter(i => search(i).toLowerCase().includes(q))
    return out
  }, [items, query, filter, search, match, sort])
  const pages = Math.max(1, Math.ceil(filtered.length / perPage))
  const current = Math.min(page, pages)
  // Reset to page 1 whenever the result set shrinks under the current page.
  useEffect(() => { setPage(1) }, [query, filter])
  return {
    query, setQuery, filter, setFilter, page: current, pages, setPage,
    rows: filtered.slice((current - 1) * perPage, current * perPage),
    total: filtered.length, all: filtered
  }
}

/** Focus a dialog's first field when it opens (popups that are forms). */
export function useAutofocus<T extends HTMLElement>(open: boolean) {
  const ref = useRef<T>(null)
  useEffect(() => { if (open) requestAnimationFrame(() => ref.current?.focus()) }, [open])
  return ref
}

/** Download a list of records as CSV (used by the audit log export). */
export function downloadCsv(filename: string, columns: string[], rows: (string | number | null)[][]): void {
  const cell = (v: string | number | null) => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const csv = [columns, ...rows].map(r => r.map(cell).join(',')).join('\n')
  const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/** Dialog footer with a secondary and a primary action (the shape every form popup uses). */
export function ModalFooter({ onCancel, onSubmit, submitLabel, cancelLabel, busy, danger, disabled }: {
  onCancel(): void; onSubmit(): void; submitLabel?: string; cancelLabel?: string; busy?: boolean; danger?: boolean; disabled?: boolean
}) {
  const { t } = useTranslation()
  return (
    <>
      <Button variant="ghost" onClick={onCancel}>{cancelLabel ?? t('common.cancel')}</Button>
      <Button variant={danger ? 'danger' : 'primary'} loading={busy} disabled={disabled} onClick={onSubmit}>
        {submitLabel ?? t('common.save')}
      </Button>
    </>
  )
}
